// MixerButton — Phase 5 Task 3.
//
// Toggles the Audio Mixer popover. Placed near the video/timeline by its
// parent (TTSView). Uses the Phosphor `ph-sliders-horizontal` icon rotated
// 90° so the slider bars read vertical — matches the legacy `.mixer-btn`
// behaviour (index.html:1235–1243 + 2263).
//
// Open/close state lives in the parent so the popover can close on outside
// click. Renders a Voice-volume warning beneath the button when the fader
// is muted, at 0%, or below 25% — talents lost recordings to a forgotten
// muted fader, the warning makes that state impossible to miss.

import { useLayoutEffect, useRef, useState } from 'react';
import { useMixerStore } from '@/stores/mixer';
import styles from './MixerPopover.module.css';

interface Props {
  isOpen: boolean;
  onToggle: (ev: React.MouseEvent) => void;
}

export function MixerButton({ isOpen, onToggle }: Props) {
  const cls = [styles.btn, isOpen ? styles.btnOpen : ''].filter(Boolean).join(' ');
  const voiceVolume = useMixerStore((s) => s.voiceVolume);
  const voiceMuted = useMixerStore((s) => s.voiceMuted);

  // Two-tier warning: mute/0 is a hard error (talent will think recording
  // is broken), under 25% is just a heads-up.
  let warning: { text: string; level: 'error' | 'warn' } | null = null;
  if (voiceMuted || voiceVolume === 0) {
    warning = { text: 'Voice volume muted', level: 'error' };
  } else if (voiceVolume < 0.25) {
    warning = { text: 'Voice volume lower than 25%', level: 'warn' };
  }

  // Dynamic placement: warning sits LEFT of the button by default. When the
  // ctrl row gets narrow enough that a left-positioned warning would
  // overlap the timecode (tagged `data-tc-anchor`), flip it BELOW the
  // button — and switch to in-flow rendering so the row gets taller
  // (pushing the minimap below) instead of overlapping it.
  //
  // Measurement strategy: render an invisible probe span with the same
  // text + the same font setup as the visible warning. The probe is always
  // absolutely positioned with white-space:nowrap so its width reliably
  // equals the natural text width regardless of which placement state the
  // visible warning is currently in (the visible warning's scrollWidth
  // shifts when in-flow-below mode forces wrap-width). Compare hypothetical
  // left-position rect against timecode rect. No setState during
  // measurement → no render loops.
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const probeRef = useRef<HTMLSpanElement | null>(null);
  const [placeBelow, setPlaceBelow] = useState(false);
  useLayoutEffect(() => {
    if (!warning) {
      setPlaceBelow(false);
      return;
    }
    const wrap = wrapRef.current;
    const probe = probeRef.current;
    if (!wrap || !probe) return;
    const tc = document.querySelector<HTMLElement>('[data-tc-anchor]');
    if (!tc) return;

    const check = () => {
      const wrapRect = wrap.getBoundingClientRect();
      const tcRect = tc.getBoundingClientRect();
      const warningW = probe.getBoundingClientRect().width;
      const GAP_PX = 8;
      const SLACK_PX = 6;
      const hypotheticalLeft = wrapRect.left - GAP_PX - warningW;
      const wouldOverlap = hypotheticalLeft < tcRect.right + SLACK_PX;
      setPlaceBelow((cur) => (cur === wouldOverlap ? cur : wouldOverlap));
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(wrap);
    ro.observe(tc);
    window.addEventListener('resize', check);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', check);
    };
  }, [warning]);

  const wrapCls = [
    styles.btnWrap,
    placeBelow ? styles.btnWrapBelow : '',
  ].filter(Boolean).join(' ');
  const warningCls = [
    warning?.level === 'error' ? styles.btnWarningError : styles.btnWarning,
    placeBelow ? styles.btnWarningInFlow : '',
  ].filter(Boolean).join(' ');

  return (
    <span ref={wrapRef} className={wrapCls}>
      <button
        type="button"
        className={cls}
        onClick={onToggle}
        title="Audio-Mixer"
        aria-label="Audio Mixer"
        aria-expanded={isOpen}
      >
        <i className={`ph-bold ph-sliders-horizontal ${styles.btnIcon}`} />
        <span>Audio Mixer</span>
      </button>
      {warning && (
        <>
          <span className={warningCls}>{warning.text}</span>
          {/* Hidden width probe: always absolute + nowrap so its rendered
              width equals the natural text width regardless of which
              placement the visible warning is in. */}
          <span ref={probeRef} className={styles.btnWarningProbe} aria-hidden="true">
            {warning.text}
          </span>
        </>
      )}
    </span>
  );
}
